<template>
  <Panel header="Datos del Estudiante" class="student-panel" :loading="loading">
    <div v-if="loading" class="loading-overlay">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <div class="info-grid">
      <!-- Institution Info -->
      <div class="info-card">
        <div class="info-header">
          <i class="pi pi-building"></i>
          <h4>Institución</h4>
        </div>
        <div class="info-content" :title="student.institucioneducativa">
          {{ student.institucioneducativa || "--" }}
          <small v-if="student.departamento"
            >({{ student.departamento }})</small
          >
        </div>
      </div>

      <!-- Codes -->
      <div class="info-card">
        <div class="info-header">
          <i class="pi pi-id-card"></i>
          <h4>Códigos</h4>
        </div>
        <div class="codes-grid">
          <div>
            <span class="code-label">SIE:</span>
            <span class="code-value">{{ student.rue || "--" }}</span>
          </div>
          <div>
            <span class="code-label">RUDE:</span>
            <span class="code-value">{{ student.codigo_rude || "--" }}</span>
          </div>
        </div>
      </div>

      <!-- Student Info -->
      <div class="info-card">
        <div class="info-header">
          <i class="pi pi-user"></i>
          <h4>Datos Personales</h4>
        </div>
        <div class="details-grid">
          <div>
            <span class="detail-label">Nombre completo:</span>
            <span class="detail-value" :title="fullName">
              {{ fullName }}
            </span>
          </div>
          <div>
            <span class="detail-label">CI:</span>
            <span class="detail-value">
              {{ student.carnet_identidad || "--" }}
              <small v-if="student.complemento">{{
                student.complemento
              }}</small>
            </span>
          </div>
          <div>
            <span class="detail-label">Fecha nacimiento:</span>
            <span class="detail-value">
              {{ formattedBirthDate }}
              <small v-if="age">({{ age }} años)</small>
            </span>
          </div>
          <div>
            <span class="detail-label">Género:</span>
            <span class="detail-value">
              {{ genderName }}
            </span>
          </div>
        </div>
      </div>

      <!-- Academic Info -->
      <div class="info-card">
        <div class="info-header">
          <i class="pi pi-book"></i>
          <h4>Información Académica</h4>
        </div>
        <div class="details-grid">
          <div>
            <span class="detail-label">Nivel:</span>
            <span class="detail-value">
              {{ nivelName }}
            </span>
          </div>
          <div>
            <span class="detail-label">Grado:</span>
            <span class="detail-value">
              {{ gradoName }}
            </span>
          </div>
          <div>
            <span class="detail-label">Paralelo:</span>
            <span class="detail-value">
              {{ paraleloName }}
            </span>
          </div>
          <!-- <div>
            <span class="detail-label">Gestión:</span>
            <span class="detail-value">
              {{ student.gestion_tipo_id || "--" }}
            </span>
          </div> -->
        </div>
      </div>
    </div>
  </Panel>
</template>

<script setup>
import { computed } from "vue";
import ProgressSpinner from "primevue/progressspinner";

const props = defineProps({
  student: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

// Computed properties
const fullName = computed(() => {
  const { nombre, paterno, materno } = props.student;
  return [nombre, paterno, materno].filter(Boolean).join(" ") || "--";
});

const formattedBirthDate = computed(() => {
  if (!props.student.fecha_nacimiento) return "--";
  try {
    const date = new Date(props.student.fecha_nacimiento);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return props.student.fecha_nacimiento || "--";
  }
});

const age = computed(() => {
  if (!props.student.fecha_nacimiento) return null;
  try {
    const birthDate = new Date(props.student.fecha_nacimiento);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  } catch {
    return null;
  }
});

const genderName = computed(() => {
  const genders = {
    1: "Masculino",
    2: "Femenino",
    3: "Otro",
  };
  return (
    genders[props.student.genero_tipo_id] ||
    props.student.genero_tipo_id ||
    "--"
  );
});

const nivelName = computed(() => {
  const niveles = {
    1: "Inicial",
    2: "Primaria",
    3: "Secundaria",
    12: "Primaria Comunitaria Vocacional",
    13: "Secundaria Comunitaria Productiva",
  };
  return (
    niveles[props.student.nivel_tipo_id] || props.student.nivel_tipo_id || "--"
  );
});

const gradoName = computed(() => {
  const grados = {
    1: "Primero",
    2: "Segundo",
    3: "Tercero",
    4: "Cuarto",
    5: "Quinto",
    6: "Sexto",
  };
  return (
    grados[props.student.grado_tipo_id] || props.student.grado_tipo_id || "--"
  );
});

const paraleloName = computed(() => {
  const paralelos = {
    1: "A",
    2: "B",
    3: "C",
    4: "D",
  };
  return (
    paralelos[props.student.paralelo_tipo_id] ||
    props.student.paralelo_tipo_id ||
    "--"
  );
});
</script>

<style scoped>
.student-panel {
  position: relative;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
  border-radius: 12px;
}

.student-panel :deep(.p-panel-header) {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  font-weight: 600;
  color: #2c3e50;
  padding: 1rem 1.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 1rem;
}

.info-card {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.info-header i {
  font-size: 1.25rem;
}

.info-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.info-content {
  font-size: 1rem;
  line-height: 1.5;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.info-content small {
  color: #6c757d;
  margin-left: 0.5rem;
}

.codes-grid,
.details-grid {
  display: grid;
  gap: 0.75rem;
}

.code-label,
.detail-label {
  font-weight: 500;
  color: #6c757d;
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.code-value,
.detail-value {
  font-weight: 500;
  color: #2c3e50;
  word-break: break-word;
}

.detail-value small {
  color: #6c757d;
  font-weight: normal;
  margin-left: 0.5rem;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
